/**
 * Retourne l'URL de l'image principale d'un produit.
 * 
 * Logique :
 * 1. Si product.images est une string non vide, on l'utilise.
 * 2. Si product.images est un tableau non vide, on utilise le premier élément.
 * 3. Sinon, on cherche une image locale dans /images/products/<ref>.jpg
 * 4. Fallback sur /images/products/default-product.jpg si pas de ref ou autre.
 * 
 * @param {Object} product - L'objet produit
 * @returns {string} - L'URL de l'image
 */
export const getProductImageUrl = (product) => {
  if (!product) return '/images/products/default-product.jpg';

  const normalizeAssetUrl = (url) => {
    if (typeof url !== 'string') return url;
    const trimmed = url.trim();
    if (!trimmed) return trimmed;

    try {
      if (typeof window === 'undefined') return trimmed;

      const pageIsHttps = window.location?.protocol === 'https:';
      const pageHost = window.location?.host;

      if (/^\/\//.test(trimmed)) {
        return pageIsHttps ? `https:${trimmed}` : trimmed;
      }

      if (/^https?:\/\//i.test(trimmed)) {
        const parsed = new URL(trimmed);

        // If the asset is served from the same host, prefer a relative URL.
        if (pageHost && parsed.host === pageHost) {
          return `${parsed.pathname}${parsed.search}${parsed.hash}`;
        }

        // Avoid mixed content on https pages.
        if (pageIsHttps && parsed.protocol === 'http:') {
          parsed.protocol = 'https:';
          return parsed.toString();
        }
      }
    } catch {
      // ignore and return the original string
    }

    return trimmed;
  };

  // 1 & 2. Vérification du champ images existant
  if (product.images) {
    if (typeof product.images === 'string' && product.images.trim() !== '') {
      return normalizeAssetUrl(product.images);
    }
    if (Array.isArray(product.images) && product.images.length > 0) {
      // On s'assure que le premier élément est une string non vide
      const firstImage = product.images[0];
      if (typeof firstImage === 'string' && firstImage.trim() !== '') {
        return normalizeAssetUrl(firstImage);
      }
    }
  }

  // 3. Construction de l'URL locale à partir de la référence
  if (product.ref) {
    // Convention : /images/products/<ref>.jpg
    return `/images/products/${product.ref}.jpg`;
  }

  // 4. Fallback
  return '/images/products/default-product.jpg';
};

/**
 * Retourne un tableau d'URLs d'images pour un produit.
 * Utile pour les galeries sur la fiche produit.
 * 
 * @param {Object} product 
 * @returns {string[]}
 */
export const getProductImages = (product) => {
  if (!product) return ['/images/products/default-product.jpg'];

  const normalizeAssetUrl = (url) => {
    if (typeof url !== 'string') return url;
    const trimmed = url.trim();
    if (!trimmed) return trimmed;

    try {
      if (typeof window === 'undefined') return trimmed;

      const pageIsHttps = window.location?.protocol === 'https:';
      const pageHost = window.location?.host;

      if (/^\/\//.test(trimmed)) {
        return pageIsHttps ? `https:${trimmed}` : trimmed;
      }

      if (/^https?:\/\//i.test(trimmed)) {
        const parsed = new URL(trimmed);

        if (pageHost && parsed.host === pageHost) {
          return `${parsed.pathname}${parsed.search}${parsed.hash}`;
        }

        if (pageIsHttps && parsed.protocol === 'http:') {
          parsed.protocol = 'https:';
          return parsed.toString();
        }
      }
    } catch {
      // ignore and return the original string
    }

    return trimmed;
  };

  if (product.images) {
    if (typeof product.images === 'string' && product.images.trim() !== '') {
      return [normalizeAssetUrl(product.images)];
    }
    if (Array.isArray(product.images) && product.images.length > 0) {
      return product.images
        .filter((u) => typeof u === 'string' && u.trim() !== '')
        .map((u) => normalizeAssetUrl(u));
    }
  }

  if (product.ref) {
    // Par défaut on retourne au moins l'image principale basée sur la ref
    return [`/images/products/${product.ref}.jpg`];
  }

  return ['/images/products/default-product.jpg'];
};
